package pl.nickers.match3.ecs

import pl.nickers.match3.SoundEvent

internal class SwapResolveSystem(
    private val pieceSet: PieceSet = PieceSetLoader.default(),
) : BaseSystem() {
    private lateinit var posMapper: ComponentMapper<GridPositionComponent>
    private lateinit var swappingMapper: ComponentMapper<SwappingComponent>
    private lateinit var activatedLaserMapper: ComponentMapper<ActivatedLaserComponent>
    private lateinit var fusedBombMapper: ComponentMapper<FusedBombComponent>
    private lateinit var activatedSwirlMapper: ComponentMapper<ActivatedSwirlComponent>
    private lateinit var fullBoardMapper: ComponentMapper<FullBoardExplosionComponent>
    private lateinit var pendingSwapNewMapper: ComponentMapper<PendingSwapComponent>
    private lateinit var justMovedMapper: ComponentMapper<JustMovedComponent>

    override fun initialize() {
        posMapper = world.mapper()
        swappingMapper = world.mapper()
        activatedLaserMapper = world.mapper()
        fusedBombMapper = world.mapper()
        activatedSwirlMapper = world.mapper()
        fullBoardMapper = world.mapper()
        pendingSwapNewMapper = world.mapper()
        justMovedMapper = world.mapper()
    }

    override fun processSystem() {
        val board = world.boardState() ?: return
        if (board.phase != GamePhase.RESOLVE || board.currentSubphase != ResolveSubphase.SWAP) return
        val pending = world.getEntitiesForAspect(Aspect.all(PendingSwapComponent::class))
        if (pending.size != 2) return
        val eA = pending[0]
        val eB = pending[1]
        val psA = pendingSwapNewMapper[eA]!!
        val psB = pendingSwapNewMapper[eB]!!

        posMapper[eA]!!.row = psA.toRow
        posMapper[eA]!!.col = psA.toCol
        posMapper[eB]!!.row = psB.toRow
        posMapper[eB]!!.col = psB.toCol
        pendingSwapNewMapper.remove(eA)
        pendingSwapNewMapper.remove(eB)
        board.didWork = true

        val isReturning = psA.isReturningSwap || psB.isReturningSwap
        if (isReturning) return

        justMovedMapper.set(eA, JustMovedComponent())
        justMovedMapper.set(eB, JustMovedComponent())

        // Effect kind of each swap partner, taken from the piece definition.
        // Pieces that don't activate on swap (e.g. regular jellies) get null.
        val effectA = world.onActivateOf(eA, pieceSet)
            ?.takeIf { world.hasActivation(eA, pieceSet, Activation.SWAP_WITH_NEIGHBOUR) }?.effect
        val effectB = world.onActivateOf(eB, pieceSet)
            ?.takeIf { world.hasActivation(eB, pieceSet, Activation.SWAP_WITH_NEIGHBOUR) }?.effect
        val hasBombA = effectA == EffectKind.AREA_BLAST_3X3
        val hasBombB = effectB == EffectKind.AREA_BLAST_3X3
        val hasLaserA = effectA == EffectKind.LINE_CLEAR
        val hasLaserB = effectB == EffectKind.LINE_CLEAR
        val hasSwirlA = effectA == EffectKind.COLLECT_COLOUR
        val hasSwirlB = effectB == EffectKind.COLLECT_COLOUR
        if (hasBombA || hasBombB || hasLaserA || hasLaserB || hasSwirlA || hasSwirlB) {
            if (hasLaserA && hasLaserB) {
                activateLaser(
                    world = world,
                    entityId = eA,
                    pieceSet = pieceSet,
                    activatedLaserMapper = activatedLaserMapper,
                    directionsOverride = setOf(LaserDirection.HORIZONTAL, LaserDirection.VERTICAL),
                    forcedConsumedEntities = setOf(eB),
                )
            } else if (hasLaserA) {
                activateLaser(world, eA, pieceSet, activatedLaserMapper)
            } else if (hasBombA || hasSwirlA) {
                fusedBombMapper.set(eA, FusedBombComponent())
            }
            if (hasLaserB && !hasLaserA) {
                activateLaser(world, eB, pieceSet, activatedLaserMapper)
            } else if (hasBombB || hasSwirlB) {
                fusedBombMapper.set(eB, FusedBombComponent())
            }
            if (hasBombA && hasBombB && !hasLaserA && !hasLaserB) {
                fullBoardMapper.set(eA, FullBoardExplosionComponent())
            }
            recordSwirlActivation(swirlEntity = eA, partnerEntity = eB,
                isSwirl = hasSwirlA, partnerIsSpecial = hasBombB || hasLaserB || hasSwirlB)
            recordSwirlActivation(swirlEntity = eB, partnerEntity = eA,
                isSwirl = hasSwirlB, partnerIsSpecial = hasBombA || hasLaserA || hasSwirlA)
            return
        }

        val hasMatch = findMatchGroups(world, board.width, board.height, pieceSet = pieceSet).isNotEmpty()
        if (!hasMatch) {
            pendingSwapNewMapper.set(eA, PendingSwapComponent(
                fromRow = psA.toRow, fromCol = psA.toCol,
                toRow = psA.fromRow, toCol = psA.fromCol,
                isReturningSwap = true,
            ))
            pendingSwapNewMapper.set(eB, PendingSwapComponent(
                fromRow = psB.toRow, fromCol = psB.toCol,
                toRow = psB.fromRow, toCol = psB.fromCol,
                isReturningSwap = true,
            ))
            swappingMapper.set(eA, SwappingComponent(
                sourceRow = psA.toRow, sourceCol = psA.toCol,
                targetRow = psA.fromRow, targetCol = psA.fromCol,
                isReturning = true,
            ))
            swappingMapper.set(eB, SwappingComponent(
                sourceRow = psB.toRow, sourceCol = psB.toCol,
                targetRow = psB.fromRow, targetCol = psB.fromCol,
                isReturning = true,
            ))
            world.enqueueAudioEvent(SoundEvent.SWAP_INVALID)
        }
    }

    /**
     * Stamps [ActivatedSwirlComponent] on a swirl whose swap partner is a regular jelly.
     * Skipped when the partner is a special tile — swirl + bomb / swirl + laser / swirl + swirl
     * combos are intentionally left to fall back on the generic fuse path (most-common-colour
     * via [EffectResolveSystem] safety-net) since their dedicated effects are future work.
     */
    private fun recordSwirlActivation(
        swirlEntity: Int,
        partnerEntity: Int,
        isSwirl: Boolean,
        partnerIsSpecial: Boolean,
    ) {
        if (!isSwirl || partnerIsSpecial) return
        if (!world.isFamily(partnerEntity, pieceSet, Family.JELLY)) return
        val partnerPieceId = world.pieceIdOf(partnerEntity) ?: return
        activatedSwirlMapper.set(swirlEntity, ActivatedSwirlComponent(targetPieceId = partnerPieceId))
    }
}
